import { FC, useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';

import CustomLoading from 'modules/_shared/components/CustomLoading';
import { getDocumentReplacedWithTags } from 'modules/documents/api/replaceDocumentTags';
import { useTranslate } from 'hooks/useTranslate';
import { store } from 'redux/store';
import {
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import {
  PublishIncentiveOfferDraft,
  PublisherPartner,
} from 'modules/incentivesMarket/modals/PublishIncentiveOffer/types/PublishIncentiveOfferModalContextType';
import { applyIncentivesMarketPublishContractPreviewTags } from 'modules/incentivesMarket/utils/applyIncentivesMarketPublishContractPreviewTags';
import { applyIncentivesMarketAcceptOfferContractPreviewTags } from 'modules/incentivesMarket/utils/applyIncentivesMarketAcceptOfferContractPreviewTags';

export type IncentivesMarketPurchaseContractPreviewProps = {
  templateId: string;
  societyId: string;
  beneficiaryId: string;
  language: string;
  operationReferencePlaceholder: string;
  offerType: IncentiveOfferType;
  /** Por defecto sustituye solo el publicante; en 'accept' rellena comprador y vendedor. */
  previewVariant?: 'publish' | 'accept';
  /** Modo publicar */
  publisherPartner?: PublisherPartner | null;
  incentiveOffer?: PublishIncentiveOfferDraft | null;
  /** Modo aceptar: datos del participante (formulario paso 2) */
  participantPartner?: Partial<IncentivesMarketBeneficiary> | null;
  purchaseQuantity?: number;
  unitPrice?: number;
  totalPrice?: number;
  contractClassName?: string;
};

const IncentivesMarketPurchaseContractPreview: FC<
  IncentivesMarketPurchaseContractPreviewProps
> = ({
  templateId,
  societyId,
  beneficiaryId,
  language,
  operationReferencePlaceholder,
  offerType,
  previewVariant = 'publish',
  publisherPartner = null,
  incentiveOffer = null,
  participantPartner = null,
  purchaseQuantity = 0,
  unitPrice = 0,
  totalPrice,
  contractClassName = '',
}) => {
  const { t } = useTranslate();
  const [baseHtml, setBaseHtml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!templateId || !societyId || !beneficiaryId) {
        setIsError(true);
        setIsLoading(false);
        setBaseHtml(null);
        return;
      }

      setIsLoading(true);
      setIsError(false);

      try {
        const decoded = (await store.dispatch(
          getDocumentReplacedWithTags({
            documentId: templateId,
            sources: {
              society: societyId,
              partner: beneficiaryId,
            },
            language: language?.split('-')[0] || 'es',
          })
        )) as { html?: string } | undefined;

        if (cancelled) return;

        const rawHtml = decoded?.html;
        if (typeof rawHtml !== 'string' || !rawHtml.trim()) {
          setIsError(true);
          setBaseHtml(null);
          return;
        }

        setBaseHtml(rawHtml);
        setIsError(false);
      } catch {
        if (!cancelled) {
          setIsError(true);
          setBaseHtml(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [templateId, societyId, beneficiaryId, language]);

  const previewHtml = useMemo(() => {
    if (!baseHtml) return null;
    if (previewVariant === 'accept') {
      return applyIncentivesMarketAcceptOfferContractPreviewTags({
        html: baseHtml,
        language,
        operationReferencePlaceholder,
        offerType,
        publisherPartner,
        participantPartner,
        purchaseQuantity,
        unitPrice,
        totalPrice,
        className: contractClassName,
      });
    }
    return applyIncentivesMarketPublishContractPreviewTags({
      html: baseHtml,
      offerType,
      publisherPartner,
      incentiveOffer,
      language,
      operationReferencePlaceholder,
    });
  }, [
    baseHtml,
    previewVariant,
    offerType,
    publisherPartner,
    incentiveOffer,
    participantPartner,
    purchaseQuantity,
    unitPrice,
    totalPrice,
    contractClassName,
    language,
    operationReferencePlaceholder,
  ]);

  if (isLoading) {
    return <CustomLoading />;
  }

  if (isError || !previewHtml) {
    return (
      <p className="text-soft mb-0 px-3 py-2">
        {t('ErrorNoIncentivesMarketTemplate')}
      </p>
    );
  }

  return (
    <div className="card card-bordered">
      <div className="card-inner p-4 p-md-5">
        <div
          className="avoid-default-font"
          style={{ maxHeight: '70vh', overflow: 'auto' }}
        >
          {parse(previewHtml)}
        </div>
      </div>
    </div>
  );
};

export default IncentivesMarketPurchaseContractPreview;
